import { useEffect, useRef, useState, type ReactNode } from "react";
import { Rect } from "fabric";
import type { FabricImage, FabricObject } from "fabric";
import {
  Canvas,
  // FILTERS,
  TEXT_PRESETS,
  // applyFilters,
  makeText,
  // readFilters,
} from "./fabricKit";
import { Icon } from "./Icon";


export type FabricCanvas = InstanceType<typeof Canvas>;
export type Timer = ReturnType<typeof setTimeout>;

export interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
  radius: number;
}

/** A Fabric object plus the custom properties this editor adds (see EXTRA_PROPS in fabricKit). */
export type StudioObject = FabricObject & {
  id?: string;
  name?: string;
  locked?: boolean;
  placeholder?: string | boolean;
  frame?: Frame;
  isBackgroundImage?: boolean;
  isEditing?: boolean;
  // text properties, only read once the object is known to be text
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string | number;
  fontStyle: string;
  underline: boolean;
  textAlign: string;
  charSpacing: number;
  lineHeight: number;
  textBackgroundColor: string;
  rx?: number;
  ry?: number;
  initDimensions?: () => void;
};

export interface DesignDetail {
  id: number | string;
  name: string;
  width: number;
  height: number;
  formatKey?: string;
  templateId?: number | string | null;
  canvasJson?: string;
  galleryId?: number | string | null;
}

export interface TemplateLite {
  id: number | string;
  specJson: string;
}

export interface MediaItem {
  mediaUrl: string;
  [key: string]: unknown;
}

export interface FilterValues {
  brightness: number;
  contrast: number;
  saturation: number;
  blur: number;
  grayscale: boolean;
}

export interface FilterDef {
  key: Exclude<keyof FilterValues, "grayscale">;
  label: string;
  min: number;
  max: number;
}

export type TextPreset = Parameters<typeof makeText>[0] & {
  key: string;
  label: string;
  family: string;
  weight: number | string;
};

export type Panel = "text" | "elements" | "uploads" | "background" | "layers";
export type SaveState = "saved" | "dirty" | "saving" | "error";
export type ToastKind = "good" | "bad" | "info";
export type ExportFormat = "jpeg" | "png";

export interface DesignEditorProps {
  designId?: number | string;
  onClose?: () => void;
}

/* ------------------------------------------------------------ constants */

export const SWATCHES = ["#ffffff", "#10231e", "#0b7a5a", "#25d366", "#1d6fa5", "#7c3aed", "#db2777", "#e11d48", "#f97316", "#ffd166", "#f5efe6", "#6b7280"];
export const GRADIENTS: [string, string][] = [
  ["#0b3d2e", "#0b7a5a"], ["#ff7a45", "#d6336c"], ["#2b0a3d", "#6a1b4d"], ["#1d6fa5", "#38bdf8"],
  ["#f59e0b", "#db2777"], ["#111827", "#374151"], ["#fde68a", "#fca5a5"], ["#a7f3d0", "#93c5fd"],
];
export const EMOJI = ["🎉", "🔥", "⭐", "❤️", "✅", "🛍️", "🎁", "📦", "🪔", "🌸", "☕", "🍕", "📞", "💬", "👉", "⏰", "📍", "💯"];
export const HISTORY_LIMIT = 60;
export const AUTOSAVE_MS = 2500;

export const RAIL = [
  ["text", "Text", "text"],
  ["elements", "Elements", "shapes"],
  ["uploads", "Photos", "upload"],
  ["background", "Background", "background"],
  ["layers", "Layers", "layers"],
] as const;

export const SHAPES = [
  ["rect", "Square"], ["rounded", "Rounded"], ["pill", "Button"], ["circle", "Circle"],
  ["triangle", "Triangle"], ["star", "Star"], ["line", "Line"],
] as const;

export const ALIGNS = [
  ["left", "alignLeft"],
  ["center", "alignCenter"],
  ["right", "alignRight"],
] as const;

export const PRESETS = TEXT_PRESETS as unknown as TextPreset[];
// export const FILTER_DEFS = FILTERS as unknown as FilterDef[];

export const errMessage = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === "string" ? e : "Something went wrong";


export const btnMd =
  "inline-flex items-center justify-center gap-2 min-h-[40px] px-3.5 py-[9px] rounded-[10px] border border-st-line bg-white text-st-ink font-semibold text-[14px] leading-none whitespace-nowrap cursor-pointer hover:enabled:border-[#9fb3ab] disabled:opacity-50 disabled:cursor-not-allowed";
export const btnSm =
  "inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] rounded-[9px] border border-st-line bg-white text-st-ink font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer hover:enabled:border-[#9fb3ab] disabled:opacity-50 disabled:cursor-not-allowed";
export const btnSoftSm =
  "inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] rounded-[9px] border border-transparent bg-st-green-soft text-st-green-dark font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer hover:enabled:border-[#9fb3ab] disabled:opacity-50 disabled:cursor-not-allowed";

export const topBtn =
  "inline-flex items-center justify-center gap-2 min-h-[40px] px-3.5 py-[9px] rounded-[10px] border font-semibold text-[14px] leading-none whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed max-[820px]:px-2.5 max-[820px]:py-2 max-[820px]:text-[13px]";
export const topBtnDark = `${topBtn} bg-white/[0.08] border-white/[0.14] text-white hover:enabled:bg-white/[0.16] hover:enabled:border-white/[0.24]`;
export const topBtnAccent = `${topBtn} bg-st-wa border-st-wa text-[#062b1c] hover:enabled:bg-[#3ee07c] hover:enabled:border-[#3ee07c]`;

export const iconBtnBase =
  "inline-grid place-items-center flex-none border-0 bg-transparent cursor-pointer disabled:opacity-[.35] disabled:cursor-not-allowed";
export const iconBtn = `${iconBtnBase} w-[38px] h-[38px] rounded-[10px] text-st-muted hover:enabled:bg-st-paper hover:enabled:text-st-ink`;
export const iconBtnDanger = `${iconBtnBase} w-[38px] h-[38px] rounded-[10px] text-st-red hover:enabled:bg-[#fbe9e5] hover:enabled:text-st-red`;
export const iconBtnDark = `${iconBtnBase} w-[38px] h-[38px] rounded-[10px] text-[#c4d6cf] hover:enabled:bg-white/10 hover:enabled:text-white`;
export const iconBtnSm = `${iconBtnBase} w-[30px] h-[30px] rounded-lg text-st-muted hover:enabled:bg-st-paper hover:enabled:text-st-ink`;

export const link =
  "inline-flex items-center gap-1.5 bg-transparent border-0 py-1 px-0 text-st-green-dark font-semibold text-[14px] cursor-pointer";

export const menuItem =
  "block w-full text-left px-3 py-2.5 border-0 rounded-[9px] bg-transparent cursor-pointer text-st-ink hover:bg-st-paper";
export const menuBox =
  "absolute right-0 top-[calc(100%+8px)] z-30 w-[290px] p-1.5 rounded-[14px] bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.4),0_0_0_1px_rgba(16,35,30,0.06)] max-[820px]:right-2 max-[820px]:top-[104px]";

export const railBtn =
  "flex flex-col items-center gap-1 px-0.5 py-2.5 border-0 rounded-[10px] text-[11px] font-medium cursor-pointer max-[820px]:flex-1 max-[820px]:min-w-[64px]";
export const railOn =
  "text-white bg-st-night-2 shadow-[inset_3px_0_0_#25d366] max-[820px]:shadow-[inset_0_-3px_0_#25d366]";
export const railOff = "text-[#a9c5ba] bg-transparent hover:text-white hover:bg-white/[0.06]";

export const panelTitle = "mb-3.5 font-display font-bold text-[19px]";
export const panelH3 =
  "mt-[22px] mb-2.5 text-[13px] font-bold text-st-muted uppercase tracking-[0.06em]";
export const hint = "mt-2 text-[13px] leading-normal text-st-muted";
export const hintTop = "mb-3 text-[13px] leading-normal text-st-muted";
export const callout =
  "mb-3 px-3 py-2.5 rounded-[10px] bg-st-green-soft text-st-green-dark text-[13px] font-semibold";

export const textPreset =
  "block w-full text-left mb-2 px-3.5 py-3 rounded-[10px] border border-st-line bg-st-paper text-st-ink cursor-pointer font-poppins hover:border-st-green";
export const fontCard =
  "min-h-[74px] p-2 rounded-[10px] border border-st-line bg-white text-[20px] text-st-ink cursor-pointer leading-[1.1] hover:border-st-green hover:bg-st-green-soft";
export const shapeBtn =
  "aspect-square grid place-items-center rounded-[10px] border border-st-line bg-st-paper text-[#3c5a50] cursor-pointer hover:border-st-green hover:text-st-green";

export const swatchBase =
  "aspect-square w-full border border-[rgba(16,35,30,0.18)] cursor-pointer p-0 relative";
export const swatch = `${swatchBase} rounded-full`;
export const swatchLg = `${swatchBase} rounded-xl`;
export const swatchCustom = `${swatchBase} rounded-full block overflow-hidden bg-[conic-gradient(#e11d48,#f97316,#ffd166,#25d366,#38bdf8,#7c3aed,#e11d48)]`;

export const checkRow = "flex items-center gap-2 text-[14px] cursor-pointer my-2.5";
export const checkInput = "w-[17px] h-[17px] accent-st-green flex-none";

export const selectBase =
  "min-h-[36px] pl-2.5 pr-[30px] py-1.5 cursor-pointer text-[14px] rounded-[9px] border border-st-line bg-white text-st-ink";

export const seg =
  "inline-flex p-[3px] gap-0.5 rounded-[10px] bg-st-paper border border-st-line";
export const segBtn =
  "min-w-8 h-[30px] px-2 border-0 rounded-[7px] bg-transparent text-st-muted text-[14px] cursor-pointer inline-grid place-items-center aria-pressed:bg-white aria-pressed:text-st-ink aria-pressed:shadow-[0_1px_3px_rgba(16,35,30,0.15)]";

// ------------------------------------------------------------------ image tools

export interface ImageToolsProps {
  img: StudioObject;
  canvas: FabricCanvas;
  onChange: () => void;
  onReplace: () => void;
}

export const asImage = (o: StudioObject) => o as unknown as FabricImage;

// export function ImageTools({ img, canvas, onChange, onReplace }: ImageToolsProps) {
//   const [v, setV] = useState<FilterValues>(() => readFilters(asImage(img)) as FilterValues);
//   useEffect(() => { setV(readFilters(asImage(img)) as FilterValues); }, [img]);
//   const update = (patch: Partial<FilterValues>) => {
//     const next = { ...v, ...patch };
//     setV(next);
//     applyFilters(asImage(img), next);
//     canvas.requestRenderAll();
//     onChange();
//   };
//   const clip = img.clipPath as Rect | undefined;
//   return (
//     <>
//       <button className={btnSm} onClick={onReplace}><Icon name="image" size={16} /> {img.frame ? "Replace photo" : "Add another photo"}</button>
//       <Popover label="Adjust">
//         {FILTER_DEFS.map((f) => (
//           <Slider key={f.key} label={f.label} value={v[f.key]} min={f.min} max={f.max} onChange={(x) => update({ [f.key]: x })} />
//         ))}
//         <label className={checkRow}><input className={checkInput} type="checkbox" checked={v.grayscale} onChange={(e) => update({ grayscale: e.target.checked })} /> Black and white</label>
//         <button className={link} onClick={() => update({ brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false })}>Reset</button>
//       </Popover>
//       <button className={btnSm} onClick={() => { img.set("flipX", !img.flipX); canvas.requestRenderAll(); onChange(); }} title="Flip"><Icon name="flipH" size={16} /> Flip</button>
//       {!img.frame && !img.isBackgroundImage && (
//         <Popover label="Corners">
//           <Slider
//             label="Rounding"
//             value={Math.round((clip?.rx || 0) * img.scaleX)}
//             min={0}
//             max={Math.round(Math.min(img.getScaledWidth(), img.getScaledHeight()) / 2)}
//             onChange={(r) => {
//               img.clipPath = r
//                 ? new Rect({
//                     width: img.width, height: img.height, rx: r / img.scaleX, ry: r / img.scaleY, originX: "center", originY: "center",
//                   })
//                 : undefined;
//               img.dirty = true;
//               canvas.requestRenderAll();
//               onChange();
//             }}
//           />
//         </Popover>
//       )}
//     </>
//   );
// }

// ------------------------------------------------------------------ small controls

export function NameField({ value, onSave }: { value: string; onSave: (name: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input
      className="w-[clamp(120px,22vw,300px)] bg-transparent border border-transparent text-white rounded-lg px-[9px] py-[7px] font-semibold text-[15px] text-ellipsis hover:border-white/[0.22] focus:bg-white/[0.08] focus:border-white/35 focus:outline-none max-[820px]:w-[140px]"
      value={v}
      aria-label="Design name"
      maxLength={120}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== value) onSave(v); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setV(value); e.currentTarget.blur(); }
      }}
    />
  );
}

export interface NumberStepperProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
}

export function NumberStepper({ value, min, max, onChange, label }: NumberStepperProps) {
  const [v, setV] = useState<string | number>(value);
  useEffect(() => setV(value), [value]);
  const commit = (n: string | number) => {
    const x = Math.min(max, Math.max(min, Number(n) || min));
    setV(x);
    onChange(x);
  };
  const stepBtn = "w-7 h-full border-0 bg-st-paper cursor-pointer grid place-items-center text-st-muted";
  return (
    <div className="inline-flex items-center border border-st-line rounded-[9px] overflow-hidden h-9">
      <button className={stepBtn} onClick={() => commit(value - (value > 40 ? 4 : 1))} aria-label={`Decrease ${label}`}><Icon name="minus" size={14} /></button>
      <input
        className="w-11 h-full border-0 text-center text-[14px] tabular-nums"
        value={v}
        aria-label={label}
        inputMode="numeric"
        onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
        onBlur={() => commit(v)}
        onKeyDown={(e) => e.key === "Enter" && commit(v)}
      />
      <button className={stepBtn} onClick={() => commit(value + (value >= 40 ? 4 : 1))} aria-label={`Increase ${label}`}><Icon name="plus" size={14} /></button>
    </div>
  );
}

export interface ColorButtonProps {
  value: unknown;
  onChange: (hex: string) => void;
  label: string;
  letter?: boolean;
  ring?: boolean;
}

export function ColorButton({ value, onChange, label, letter, ring }: ColorButtonProps) {
  const hex = toHex(value);
  return (
    <label className="relative w-9 h-9 grid place-items-center rounded-[9px] border border-st-line cursor-pointer bg-white" title={label}>
      {letter ? (
        <span className="font-poppins font-bold text-[17px] leading-none border-b-4 px-0.5 pb-px" style={{ borderBottomColor: hex }}>A</span>
      ) : (
        <span
          className={`w-5 h-5 rounded-full ${ring ? "border-4 bg-transparent" : "border border-black/[0.18]"}`}
          style={ring ? { borderColor: hex } : { background: hex }}
        />
      )}
      <input className="absolute inset-0 opacity-0 cursor-pointer" type="color" value={hex} onChange={(e) => onChange(e.target.value)} aria-label={label} />
    </label>
  );
}

export interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  suffix?: string;
}

export function Slider({ label, value, min, max, onChange, suffix = "" }: SliderProps) {
  return (
    <div className="my-3">
      <div className="flex justify-between text-[13px] mb-1"><span>{label}</span><span className="text-st-muted tabular-nums">{value}{suffix}</span></div>
      <input className="w-full accent-st-green" type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} />
    </div>
  );
}

export interface PopoverProps {
  label: ReactNode;
  title?: string;
  align?: "left" | "right";
  children: ReactNode;
}

export function Popover({ label, title, align = "left", children }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button className={btnSm} aria-expanded={open} onClick={() => setOpen(!open)} title={title}>{label}</button>
      {open && (
        <div
          className={`absolute top-[calc(100%+8px)] z-40 w-[260px] px-3.5 py-3 rounded-[14px] bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35),0_0_0_1px_rgba(16,35,30,0.06)] ${align === "right" ? "right-0" : "left-0"}`}
          role="dialog"
          aria-label={title || (typeof label === "string" ? label : "Options")}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export type GlyphKind = "rect" | "rounded" | "pill" | "circle" | "triangle" | "star" | "line" | "frame" | "circleFrame";

export function ShapeGlyph({ kind }: { kind: GlyphKind }) {
  const common = { fill: "currentColor" };
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
      {kind === "rect" && <rect x="8" y="8" width="32" height="32" {...common} />}
      {kind === "rounded" && <rect x="4" y="12" width="40" height="24" rx="6" {...common} />}
      {kind === "pill" && <rect x="2" y="16" width="44" height="16" rx="8" {...common} />}
      {kind === "circle" && <circle cx="24" cy="24" r="17" {...common} />}
      {kind === "triangle" && <path d="M24 7l18 33H6z" {...common} />}
      {kind === "star" && <path d="M24 5l5.6 12.3 13.4 1.4-10 9 2.9 13.2L24 34.2 12.1 40.9 15 27.7l-10-9 13.4-1.4z" {...common} />}
      {kind === "line" && <path d="M5 24h38" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />}
      {kind === "frame" && <rect x="8" y="8" width="32" height="32" rx="5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="5 4" />}
      {kind === "circleFrame" && <circle cx="24" cy="24" r="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="5 4" />}
    </svg>
  );
}

export function layerName(o: StudioObject): string {
  if (o.isBackgroundImage) return "Background photo";
  const t = (o.type || "").toLowerCase();
  if (t === "textbox") return (o.text || "Text").split("\n")[0].slice(0, 28) || "Text";
  return o.name || t;
}

export function layerIcon(o: StudioObject): string {
  const t = (o.type || "").toLowerCase();
  if (t === "textbox") return "T";
  if (t === "image") return "▣";
  if (o.placeholder) return "⬚";
  return "◆";
}

export function toHex(c: unknown): string {
  if (!c || typeof c !== "string") return "#000000";
  if (/^#[0-9a-f]{6}$/i.test(c)) return c;
  if (/^#[0-9a-f]{3}$/i.test(c)) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (m) return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
  return "#000000";
}